import { useState, useMemo } from "react";
import { Button, Form, Icon, Label, Message, Modal, Segment } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "./DataTable";

const EMPTY_GUEST_HOUSE = {
  name: "",
  shortName: "",
  address: "",
  contactEmail: "",
  contactPhone: "",
  isActive: true,
  geoLocationLink: "",
};

const STATUS_OPTIONS = [
  { key: "active", text: "Active", value: "active" },
  { key: "inactive", text: "Inactive", value: "inactive" },
];

const URL_RE = /^https?:\/\/.+/i;

const fieldError = (message) => (message ? { content: message, pointing: "below" } : false);

function GuestHouseFormFields({ form, errors, onChange }) {
  return (
    <Form error={!!errors.save}>
      <Form.Group widths="equal">
        <Form.Input
          label="Name"
          required
          placeholder="e.g. Science Block Guest House"
          value={form.name}
          error={fieldError(errors.name)}
          onChange={(e) => onChange("name", e.target.value)}
        />
        <Form.Input
          label="Short Code"
          placeholder="e.g. SBG"
          maxLength={10}
          value={form.shortName}
          onChange={(e) => onChange("shortName", e.target.value.toUpperCase())}
        />
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Input
          label="Address"
          placeholder="e.g. Science Campus, Building C"
          value={form.address}
          onChange={(e) => onChange("address", e.target.value)}
        />
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Input
          label="Contact Email"
          type="email"
          placeholder="e.g. sbgh@university.edu"
          value={form.contactEmail}
          onChange={(e) => onChange("contactEmail", e.target.value)}
        />
        <Form.Input
          label="Contact Phone"
          placeholder="e.g. 0411-9876543"
          value={form.contactPhone}
          onChange={(e) => onChange("contactPhone", e.target.value)}
        />
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Input
          label="Location Link"
          placeholder="e.g. https://maps.google.com/..."
          value={form.geoLocationLink}
          error={fieldError(errors.geoLocationLink)}
          onChange={(e) => onChange("geoLocationLink", e.target.value)}
        />
        <Form.Select
          label="Status"
          options={STATUS_OPTIONS}
          value={form.isActive ? "active" : "inactive"}
          onChange={(_, data) => onChange("isActive", data.value === "active")}
        />
      </Form.Group>
      {errors.save && <Message negative content={errors.save} />}
    </Form>
  );
}

const columnHelper = createColumnHelper();

export default function GuestHouseListTab({ guestHouses, onAdd, onUpdate, onDelete }) {
  /* formModal: null | { mode: "add" } | { mode: "edit", gh } */
  const [formModal, setFormModal] = useState(null);
  const [form, setForm] = useState(EMPTY_GUEST_HOUSE);
  const [originalForm, setOriginalForm] = useState(EMPTY_GUEST_HOUSE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const updateForm = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const openAdd = () => {
    setForm(EMPTY_GUEST_HOUSE);
    setErrors({});
    setFormModal({ mode: "add" });
  };

  const openEdit = (gh) => {
    const initial = {
      name: gh.name || "",
      shortName: gh.shortName || "",
      address: gh.address || "",
      contactEmail: gh.contactEmail || "",
      contactPhone: gh.contactPhone || "",
      isActive: gh.isActive !== false,
      geoLocationLink: gh.geoLocationLink || "",
    };
    setForm(initial);
    setOriginalForm(initial);
    setErrors({});
    setFormModal({ mode: "edit", gh });
  };

  const dirty = formModal?.mode !== "edit" || JSON.stringify(form) !== JSON.stringify(originalForm);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (form.geoLocationLink.trim() && !URL_RE.test(form.geoLocationLink.trim())) {
      e.geoLocationLink = "Enter a valid http(s) URL.";
    }
    return e;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (formModal.mode === "edit") {
        await onUpdate(formModal.gh.id, form);
      } else {
        await onAdd(form);
      }
      setFormModal(null);
    } catch (e) {
      setErrors({
        save: e.message || `Failed to ${formModal.mode === "edit" ? "update" : "add"} guest house.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      setDeleteError(e.message || "Failed to delete guest house.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", { header: "Name" }),
      columnHelper.accessor("address", {
        header: "Address",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("contactEmail", {
        header: "Contact Email",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("contactPhone", {
        header: "Contact Phone",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("isActive", {
        header: "Status",
        cell: (info) => (
          <Label color={info.getValue() !== false ? "green" : "grey"}>
            {info.getValue() !== false ? "Active" : "Inactive"}
          </Label>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: (info) => (
          <Button.Group size="small">
            <Button basic onClick={() => openEdit(info.row.original)}>
              <Icon name="edit" />
              Edit
            </Button>
            <Button
              basic
              negative
              onClick={() => {
                setDeleteError("");
                setDeleteConfirm(info.row.original);
              }}
            >
              <Icon name="trash" />
              Delete
            </Button>
          </Button.Group>
        ),
      }),
    ],
    [],
  );

  return (
    <div>
      <Segment basic vertical textAlign="right">
        <Button primary onClick={openAdd}>
          <Icon name="plus" />
          Add Guest House
        </Button>
      </Segment>

      <DataTable
        data={guestHouses}
        columns={columns}
        emptyMessage="No guest houses found yet."
      />

      {/* Add / Edit Modal */}
      <Modal
        open={!!formModal}
        onClose={() => setFormModal(null)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name={formModal?.mode === "edit" ? "edit" : "plus circle"} />
          {formModal?.mode === "edit" ? "Edit Guest House" : "Add Guest House"}
        </Modal.Header>
        <Modal.Content>
          <GuestHouseFormFields form={form} errors={errors} onChange={updateForm} />
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setFormModal(null)}>
            Cancel
          </Button>
          <Button primary loading={saving} disabled={saving || !dirty} onClick={handleSave}>
            <Icon name="save" />
            {saving
              ? "Saving…"
              : formModal?.mode === "edit"
                ? "Save Changes"
                : "Add Guest House"}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="mini"
      >
        <Modal.Header>
          <Icon name="trash" /> Delete Guest House
        </Modal.Header>
        <Modal.Content>
          <p>
            Delete <strong>{deleteConfirm?.name}</strong>?
          </p>
          <p className="bhu-confirm-sub">This cannot be undone.</p>
          {deleteError && <Message negative content={deleteError} />}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button negative loading={deleting} disabled={deleting} onClick={handleDelete}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
